/**
 * The board's map-asset layer (`projects/map-assets.md` 3.2): one Sprite per placed asset, its
 * texture the painted drawing for its {@link AssetLook}. It sits directly above the terrain chunks,
 * so the full grid, zones, overlays and tokens all draw over a tree.
 *
 * The asset's token still exists in the token layer, empty, because the animator, the name label,
 * the selection halo and the target rings all hang off it. The board calls {@link AssetLayer.follow}
 * each frame with that token's live position, so a GM drag or a tween carries the sprite with it.
 * A destroyed asset is unplaced (`decisions/0193` point 6), so it leaves `set`'s list and its sprite
 * goes; the `remains` terrain it painted shows through.
 *
 * The scale and seal trees draw from generated sprites (`treeSprites.ts`), everything else from the
 * painter. The sprites load behind the board: a tree draws with the painter until they land, and the
 * texturer's `refresh` then redraws every sprite.
 */
import { CanvasSource, type Container, Sprite, Texture } from "pixi.js";
import { CELL } from "../combatCells";
import { ASSET_MARGIN, assetSprite } from "./assetArt";
import { type AssetLook, type PlacedAsset, assetLookKey } from "./assetLook";
import { sharedMaterials } from "./materials";
import { type TreeSprites, sharedTreeSprites, treeArt, treeSprite } from "./treeSprites";

/** Device pixels per board pixel for asset drawings: the terrain's 2× ceiling, always. */
const ASSET_RESOLUTION = 2;

/** Paints a look into a texture. The board's paints with the atlas; tests pass a fake. */
export type AssetTexturer = (look: AssetLook) => Texture;

/** Makes the texturer. It calls `refresh` when better art lands, so every texture is made again. */
export type AssetTexturerFactory = (refresh: () => void) => Promise<AssetTexturer>;

export type AssetLayer = {
  /** Show exactly these assets, in this order (back to front). */
  set(assets: readonly PlacedAsset[]): void;
  /** Move an asset's sprite to its token's live top-left and opacity. */
  follow(id: string, at: { x: number; y: number; alpha: number }): void;
  /** The ids drawn right now, back to front. */
  drawn(): string[];
  destroy(): void;
};

/**
 * The atlas-backed texturer: loads the shared materials once, and the tree sprites on the first
 * tree it paints, which draws with the painter until they land.
 */
export async function atlasTexturer(refresh: () => void): Promise<AssetTexturer> {
  const materials = await sharedMaterials();
  let trees: TreeSprites | null = null;
  let loading = false;
  return (look) => {
    if (!trees && !loading && treeArt(look)) {
      loading = true;
      sharedTreeSprites()
        .then((t) => {
          trees = t;
          refresh();
        })
        .catch((e: unknown) => console.error("tree sprites failed to load; trees keep the painter", e));
    }
    const resource = treeSprite(look, trees, ASSET_RESOLUTION) ?? assetSprite(look, materials, ASSET_RESOLUTION);
    return new Texture({ source: new CanvasSource({ resource, resolution: ASSET_RESOLUTION }) });
  };
}

export function createAssetLayer({ container, texturer = atlasTexturer }: { container: Container; texturer?: AssetTexturerFactory }): AssetLayer {
  const sprites = new Map<string, { sprite: Sprite; key: string }>();
  // One texture per look, shared by every sprite with that look, owned by this layer.
  const textures = new Map<string, Texture>();
  let paint: AssetTexturer | null = null;
  let wanted: readonly PlacedAsset[] = [];
  let destroyed = false;

  function textureFor(look: AssetLook): Texture {
    const key = assetLookKey(look);
    let t = textures.get(key);
    if (!t) textures.set(key, (t = paint!(look)));
    return t;
  }

  function apply(): void {
    if (!paint || destroyed) return;
    const keep = new Set(wanted.map((a) => a.id));
    for (const [id, s] of sprites) {
      if (keep.has(id)) continue;
      s.sprite.destroy();
      sprites.delete(id);
    }
    for (const a of wanted) {
      const key = assetLookKey(a.look);
      let s = sprites.get(a.id);
      if (!s) {
        s = { sprite: new Sprite(textureFor(a.look)), key };
        sprites.set(a.id, s);
      } else if (s.key !== key) {
        s.sprite.texture = textureFor(a.look);
        s.key = key;
      }
      s.sprite.position.set(a.minX * CELL - ASSET_MARGIN, a.minY * CELL - ASSET_MARGIN);
      // `addChild` on a child already there moves it to the end, which restores the list's order.
      container.addChild(s.sprite);
    }
  }

  /** Make every texture again: the sprites swap first, then the old textures go. */
  function refresh(): void {
    const stale = [...textures.values()];
    textures.clear();
    for (const s of sprites.values()) s.key = "";
    apply();
    for (const t of stale) t.destroy(true);
  }

  void texturer(refresh)
    .then((t) => {
      paint = t;
      apply();
    })
    .catch((e: unknown) => console.error("map asset art failed to load", e));

  return {
    set(assets) {
      wanted = assets;
      apply();
    },
    follow(id, { x, y, alpha }) {
      const s = sprites.get(id);
      if (!s) return;
      s.sprite.position.set(x - ASSET_MARGIN, y - ASSET_MARGIN);
      s.sprite.alpha = alpha;
    },
    drawn() {
      return container.children.flatMap((child) => [...sprites].filter(([, s]) => s.sprite === child).map(([id]) => id));
    },
    destroy() {
      destroyed = true;
      for (const s of sprites.values()) s.sprite.destroy();
      sprites.clear();
      for (const t of textures.values()) t.destroy(true);
      textures.clear();
    },
  };
}
